/**
 * The head the server shell rendered for the page that was requested first.
 *
 * Helmet replaces the head as soon as React renders, and views only emit their own SEO once their data has loaded.
 * Until then the head would fall back to the generic defaults and lose the canonical, which is what a crawler
 * snapshotting a still-loading page sees. Capturing the shell head lets the app keep it for that first route.
 */
export interface ShellHead {
    /** Pathname and query the shell was served for. */
    location: string;
    title: string;
    description?: string;
    canonical: string;
    ogImage?: string;
}

const meta = (selector: string) => document.head.querySelector<HTMLMetaElement>(selector)?.content || undefined;

export const readShellHead = (): ShellHead | null => {
    if (typeof document === "undefined") {
        return null;
    }

    const canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.href;

    if (!canonical || !document.title) {
        return null;
    }

    return {
        location: window.location.pathname + window.location.search,
        title: document.title,
        description: meta('meta[name="description"]'),
        canonical,
        ogImage: meta('meta[property="og:image"]'),
    };
};

// Read at module load, before React renders and Helmet starts managing the head
export const shellHead = readShellHead();
